import { Component, DestroyRef, OnInit, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { FilePondOptions } from 'filepond';
import { firstValueFrom, take } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { BookingsService } from '../../../core/services/bookings.service';
import { ToastService } from '../../../core/services/toast.service';
import { ModalService } from '../../../core/services/modal.service';
import { ApiError } from '../../../core/models/common/api-error';
import {
  BookingImportBatch,
  BookingImportReport,
  BookingImportRow,
} from '../../../core/models/entities/booking-import';
import { FileUploadComponent } from '../../../shared-manager/components/file-upload/file-upload.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { BreadcrumbItem } from '../../../shared-manager/components/page-header.component';
import { TabLink } from '../../../shared/components/tabs.component';

/**
 * Ids d'onglets (internes, en anglais) et slug du paramètre d'URL public
 * `?tab=historique`, qui reste en français car il est déjà en circulation.
 */
type ImportTab = 'import' | 'history';
const HISTORY_URL_SLUG = 'historique';

@Component({
  selector: 'app-bookings-import-page',
  template: `
    <app-page-header
      [title]="'bookings.import.page-title' | transloco"
      [breadcrumbs]="breadcrumbs"
    ></app-page-header>

    <app-spacer></app-spacer>

    <app-tabs
      [links]="tabLinks"
      [useRouter]="false"
      [activeTab]="activeTab()"
      (activeTabChange)="onTabChange($event)"
    ></app-tabs>

    <app-spacer size="6"></app-spacer>

    @if (activeTab() === 'import') {
      @if (fileErrors().length > 0) {
        <app-alert variant="danger">
          <div class="text-lg font-bold"
            >{{ 'bookings.import.errors-title' | transloco }}</div
          >
          @for (error of fileErrors(); track error) {
            <div>{{ error }}</div>
          }
        </app-alert>
        <app-spacer></app-spacer>
      }

      @if (step() === 'upload') {
        <app-card>
          <div>
            <p>{{ 'bookings.import.description-1' | transloco }}</p>
            <ul class="mt-3 list-inside list-disc space-y-1">
              <li
                [innerHTML]="'bookings.import.bullet-private-only' | transloco"
              ></li>
              <li>
                {{ 'bookings.import.bullet-template-before' | transloco }}
                <a class="cursor-pointer" (click)="downloadTemplate()"
                  ><span class="font-bold">{{
                    'bookings.import.template-link' | transloco
                  }}</span></a
                >
                {{ 'bookings.import.bullet-template-after' | transloco }}
              </li>
              <li
                [innerHTML]="'bookings.import.bullet-max-rows' | transloco"
              ></li>
            </ul>
            <p class="mt-3">{{ 'bookings.import.description-2' | transloco }}</p>
          </div>
          <app-spacer size="6"></app-spacer>
          <div class="relative">
            <app-file-upload
              [config]="fileUploadConfig"
              labelIcon="far fa-file-excel"
              [labelSubject]="'file-upload.subject-excel-file' | transloco"
              [labelPlural]="false"
              (afterAddFile)="validateFile()"
              (afterRemoveFile)="fileErrors.set([])"
            ></app-file-upload>
            @if (validating()) {
              <div
                class="absolute inset-x-0 top-20 flex items-center justify-center gap-3"
              >
                <app-loader size="sm"></app-loader>
                <span class="text-gray-500">{{
                  'bookings.import.validating' | transloco
                }}</span>
              </div>
            }
          </div>
        </app-card>
      } @else if (report(); as importReport) {
        @if (importReport.valid) {
          <app-alert variant="success" width="full">{{
            'bookings.import.valid-message' | transloco
          }}</app-alert>
        } @else {
          <app-alert variant="danger" width="full">
            <div>{{
              'bookings.import.errors-summary'
                | transloco
                  : {
                      errors: importReport.summary.errors,
                      total: importReport.summary.total,
                    }
            }}</div>
            <div class="mt-2">
              <a
                class="cursor-pointer font-bold underline"
                (click)="backToUpload()"
                >{{ 'bookings.import.restart-link' | transloco }}</a
              >
            </div>
          </app-alert>
        }

        <app-spacer></app-spacer>

        <app-table
          [data]="displayedRows(importReport)"
          [clickableRows]="false"
          [rowClass]="rowClass"
          [trackByFn]="trackByLine"
        >
          <app-table-column
            field="line"
            [header]="'bookings.import.col-line' | transloco"
            width="70px"
          >
            <ng-template let-row>{{ row.line }}</ng-template>
          </app-table-column>
          <app-table-column
            field="room_name"
            [header]="'bookings.import.col-room' | transloco"
          >
            <ng-template let-row>{{ row.room_name ?? '-' }}</ng-template>
          </app-table-column>
          <app-table-column
            field="name"
            [header]="'bookings.import.col-title' | transloco"
          >
            <ng-template let-row>{{ row.name ?? '-' }}</ng-template>
          </app-table-column>
          <app-table-column
            field="start"
            [header]="'bookings.import.col-start' | transloco"
          >
            <ng-template let-row>{{
              row.start ? (row.start | date: "EEE dd.MM.yyyy, HH'h'mm") : '-'
            }}</ng-template>
          </app-table-column>
          <app-table-column
            field="end"
            [header]="'bookings.import.col-end' | transloco"
          >
            <ng-template let-row>{{
              row.end ? (row.end | date: "EEE dd.MM.yyyy, HH'h'mm") : '-'
            }}</ng-template>
          </app-table-column>
          <app-table-column
            field="errors"
            [header]="'bookings.import.col-errors' | transloco"
          >
            <ng-template let-row>
              @if (errorsOf(row).length === 0) {
                <span class="text-gray-500">-</span>
              } @else {
                @for (error of errorsOf(row); track error) {
                  <div class="text-red-600">{{ error }}</div>
                }
              }
            </ng-template>
          </app-table-column>
        </app-table>

        @if (importReport.valid) {
          <app-spacer></app-spacer>

          <div class="flex gap-4">
            <app-button [loading]="importing()" (click)="confirmImport()">{{
              importButtonLabel(importReport)
            }}</app-button>
            <app-button variant="outlined" (click)="backToUpload()">{{
              'common.button.cancel' | transloco
            }}</app-button>
          </div>
        }
      }
    } @else {
      <div class="text-sm text-gray-500">{{
        'bookings.import.history-notice' | transloco
      }}</div>

      <app-spacer></app-spacer>

      <app-table
        [data]="history()"
        [loading]="loadingHistory()"
        (rowClicked)="openImportDetail($event)"
        [emptyStateMessage]="'bookings.import.history-empty' | transloco"
      >
        <app-table-column
          field="file_name"
          [header]="'bookings.import.col-file' | transloco"
        >
          <ng-template let-batch>{{ batch.file_name }}</ng-template>
        </app-table-column>
        <app-table-column
          field="rows_count"
          [header]="'bookings.import.col-count' | transloco"
        >
          <ng-template let-batch>{{ batch.rows_count }}</ng-template>
        </app-table-column>
        <app-table-column
          field="created_at"
          [header]="'bookings.import.col-imported-on' | transloco"
        >
          <ng-template let-batch>{{
            batch.created_at | date: 'dd.MM.yyyy HH:mm'
          }}</ng-template>
        </app-table-column>
        <app-table-column
          field="user"
          [header]="'bookings.import.col-imported-by' | transloco"
        >
          <ng-template let-batch>{{ batch.user?.name ?? '-' }}</ng-template>
        </app-table-column>
        <app-table-column
          field="rolled_back_at"
          [header]="'bookings.import.col-status' | transloco"
        >
          <ng-template let-batch>
            @if (batch.rolled_back_at) {
              <app-badge variant="gray">{{
                'bookings.import.status-cancelled' | transloco
              }}</app-badge>
            } @else {
              <app-badge variant="success">{{
                'bookings.import.status-imported' | transloco
              }}</app-badge>
            }
          </ng-template>
        </app-table-column>
        <app-table-actions>
          <ng-template let-batch>
            @if (!batch.rolled_back_at) {
              <app-table-action
                [label]="'bookings.import.rollback-action' | transloco"
                icon="trash"
                (actionClick)="rollback(batch)"
              ></app-table-action>
            }
          </ng-template>
        </app-table-actions>
      </app-table>
    }
  `,
  styles: ``,
  standalone: false,
})
export class BookingsImportPageComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private bookingsService = inject(BookingsService);
  private toastService = inject(ToastService);
  private modalService = inject(ModalService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private translocoService = inject(TranslocoService);

  fileUploadCmp = viewChild(FileUploadComponent);

  activeTab = signal<ImportTab>('import');
  step = signal<'upload' | 'preview'>('upload');
  report = signal<BookingImportReport | null>(null);
  history = signal<BookingImportBatch[]>([]);
  fileErrors = signal<string[]>([]);
  validating = signal(false);
  importing = signal(false);
  loadingHistory = signal(false);

  tabLinks: TabLink[] = [];

  breadcrumbs: BreadcrumbItem[] = [];

  fileUploadConfig: Partial<FilePondOptions> = {
    className: 'single-col',
    allowMultiple: false,
    acceptedFileTypes: [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
  };

  private fileToImport: File | null = null;

  trackByLine = (row: BookingImportRow) => row.line;

  rowClass = (row: BookingImportRow) =>
    this.errorsOf(row).length > 0 ? 'bg-red-50' : '';

  ngOnInit() {
    if (this.route.snapshot.queryParamMap.get('tab') === HISTORY_URL_SLUG) {
      this.activeTab.set('history');
    }

    this.translocoService
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.breadcrumbs = [
          {
            label: this.translocoService.translate(
              'bookings.import.breadcrumb-parent',
            ),
            routerLink: '/reservations',
          },
          {
            label: this.translocoService.translate(
              'bookings.import.breadcrumb-self',
            ),
          },
        ];
        this.tabLinks = [
          {
            id: 'import',
            label: this.translocoService.translate('bookings.import.tab-import'),
          },
          {
            id: 'history',
            label: this.translocoService.translate(
              'bookings.import.tab-history',
            ),
          },
        ];
        this.fileUploadConfig = {
          ...this.fileUploadConfig,
          labelIdle: `
            <div>
              <i class="fal fa-file-excel"></i>
              <div class="font-sans text-secondary-900">${this.translocoService.translate('bookings.import.upload-idle')}</div>
            </div>
          `,
        };
      });

    this.loadHistory();
  }

  onTabChange(tab: string | undefined) {
    if (!tab) {
      return;
    }

    this.activeTab.set(tab as ImportTab);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab === 'history' ? HISTORY_URL_SLUG : null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  async downloadTemplate() {
    const data = await this.bookingsService.downloadImportTemplate();
    const blob = new Blob([data], { type: 'application/ms-excel' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = 'Modele_Import_Reservations.xlsx';
    document.body.appendChild(link);
    link.click();
  }

  async validateFile() {
    const file = this.selectedFile();
    if (!file) {
      return;
    }

    this.fileToImport = file;
    this.fileErrors.set([]);
    this.validating.set(true);
    try {
      this.report.set(await this.bookingsService.validateImport(file));
      this.step.set('preview');
    } catch (exception) {
      this.fileErrors.set(this.flattenApiErrors(exception as ApiError));
    } finally {
      this.validating.set(false);
    }
  }

  async confirmImport() {
    const file = this.fileToImport;
    if (!file) {
      return;
    }

    this.importing.set(true);
    try {
      const batch = await this.bookingsService.runImport(file);
      this.toastService.success(
        batch.rows_count === 1
          ? this.translocoService.translate('bookings.import.toast-success-one')
          : this.translocoService.translate(
              'bookings.import.toast-success-other',
              { count: batch.rows_count },
            ),
      );
      this.backToUpload();
      await this.loadHistory();
      this.onTabChange('history');
    } catch (exception) {
      this.toastService.error((exception as ApiError).message);
      await this.refreshReportAfterFailedImport(file);
    } finally {
      this.importing.set(false);
    }
  }

  backToUpload() {
    this.fileToImport = null;
    this.report.set(null);
    this.step.set('upload');
    // @ts-ignore
    this.fileUploadCmp()?.filePond?.removeFiles();
  }

  openImportDetail(batch: BookingImportBatch) {
    this.router.navigate(['/reservations/import', batch.id]);
  }

  rollback(batch: BookingImportBatch) {
    const rollbackTitle = this.translocoService.translate(
      'bookings.import.rollback-action',
    );
    const instance = this.modalService.open(ConfirmModalComponent, {
      title: rollbackTitle,
      message:
        batch.rows_count === 1
          ? this.translocoService.translate(
              'bookings.import.rollback-confirm-one',
              { fileName: batch.file_name },
            )
          : this.translocoService.translate(
              'bookings.import.rollback-confirm-other',
              { fileName: batch.file_name, count: batch.rows_count },
            ),
      okAction: {
        title: rollbackTitle,
        variant: 'danger',
      },
      cancelAction: {
        title: this.translocoService.translate('common.button.no'),
      },
    });

    instance.okClicked$.pipe(take(1)).subscribe(async () => {
      try {
        instance.saving = true;
        await this.bookingsService.rollbackImport(batch.id);
        this.toastService.success(
          this.translocoService.translate(
            'bookings.import.rollback-toast-success',
          ),
        );
        await this.loadHistory();
      } catch (exception) {
        this.toastService.error((exception as ApiError).message);
      }
      instance.saving = false;
      this.modalService.close();
    });
  }

  errorsOf(row: BookingImportRow): string[] {
    return Object.values(row.errors).flat();
  }

  importButtonLabel(report: BookingImportReport): string {
    const total = report.summary.total;

    return total === 1
      ? this.translocoService.translate('bookings.import.confirm-btn-one')
      : this.translocoService.translate('bookings.import.confirm-btn-other', {
          count: total,
        });
  }

  displayedRows(report: BookingImportReport): BookingImportRow[] {
    if (report.valid) {
      return report.rows;
    }

    return report.rows.filter((row) => this.errorsOf(row).length > 0);
  }

  private selectedFile(): File | null {
    const file = this.fileUploadCmp()?.files?.[0];
    return file instanceof File ? file : null;
  }

  private async refreshReportAfterFailedImport(file: File) {
    try {
      this.report.set(await this.bookingsService.validateImport(file));
    } catch {
      this.backToUpload();
    }
  }

  private flattenApiErrors(exception: ApiError): string[] {
    if (exception.errors) {
      return Object.values(exception.errors).flat();
    }

    return [exception.message];
  }

  private async loadHistory() {
    this.loadingHistory.set(true);
    try {
      const response = await firstValueFrom(
        this.bookingsService.findAllImports(),
      );
      this.history.set(response.data);
    } finally {
      this.loadingHistory.set(false);
    }
  }
}
